import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from "react";
import { CopilotLog, CopilotLogsType } from "~/pages/copilotMessage/types/copilotLogs";
import { useSaveLogs } from "~/pages/copilotMessage/hooks/useSaveLogs";
import {
  getAndClearStoragedLogs,
  insertUserLogsStorage,
} from "~/storages/userlog";

interface UserLogContextProps {
  insertUserLog: (logType: CopilotLogsType) => void;
  isProgramaticScrolling: React.MutableRefObject<boolean>;
}

interface UserLogProviderProps {
  children: ReactNode;
}

let userLogs: CopilotLog[] | undefined = undefined;

const SAVE_LOGS_INTERVAL = 60000;

const UserLogContext = createContext<UserLogContextProps>({
  insertUserLog: () => {},
  isProgramaticScrolling: { current: true },
});

export default function UserLogProvider({ children }: UserLogProviderProps) {
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // COMMENT: yusuf, fatih, this is for programatic scrolling, it is used to prevent the log from being inserted when the user scrolls
  // programatic scrolling is used because of click on alert or conversation, and it is not a user interaction
  const isProgramaticScrolling = useRef<boolean>(true);

  const saveLogs = useSaveLogs();

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      const storagedLogs = getAndClearStoragedLogs();
      if (storagedLogs.length > 0) {
        saveLogs(storagedLogs);
        userLogs = undefined;
      }
    }, SAVE_LOGS_INTERVAL);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [saveLogs]);

  const insertUserLog = useCallback((logType: CopilotLogsType) => {
    const logObject = {
      actionDate: new Date().toISOString(),
      logType,
    };

    if (!userLogs) {
      userLogs = [logObject];
    } else {
      userLogs.push(logObject);
    }
    insertUserLogsStorage(userLogs);
  }, []);

  return (
    <UserLogContext.Provider value={{ insertUserLog, isProgramaticScrolling }}>
      {children}
    </UserLogContext.Provider>
  );
}
// eslint-disable-next-line react-refresh/only-export-components
export const useUserLogContext = () => useContext(UserLogContext); 